import { useState, useEffect, useCallback, useRef } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useUpdateChargingStationDetails(stationId) {
  const { send, isConnected, isOpen, connect } = useWebSocketContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const updateStation = useCallback(
    async (editableData) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");

      try {
        if (!isConnected) {
          if (!connectionAttemptedRef.current) {
            connectionAttemptedRef.current = true;
            await connect();
          }

          await new Promise((resolve, reject) => {
            const start = Date.now();
            const interval = setInterval(() => {
              if (!mountedRef.current) {
                clearInterval(interval);
                reject(new Error("Component unmounted"));
              } else if (isOpen()) {
                clearInterval(interval);
                resolve(true);
              } else if (Date.now() - start > 10000) {
                clearInterval(interval);
                reject(new Error("Connection timeout"));
              }
            }, 100);
          });
        }

        // Pull cached full station data from localStorage
        const cached =
          JSON.parse(localStorage.getItem(`station_${stationId}`)) || {};

        // Merge editable fields with cached data
        const payload = {
          ...cached,
          is_update: true,
          station_name: editableData.station_name,
          status: editableData.status,
          price_per_kwh: parseFloat(editableData.price_per_kwh),
        };
        // localStorage.removeItem('station_5')

        // Send update request
        const response = await send("cms_add_or_update_charging_station", payload);

        if (!response || response.status === "error") {
          throw new Error(response?.message || "Failed to update charging station");
        }

        if (response.success) {
          console.log("Station updated successfully:", response);
          localStorage.setItem(`station_${stationId}`, JSON.stringify(payload));
        }

        return response;
      } catch (err) {
        if (mountedRef.current) setError(err.message);
        throw err;
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [stationId, isConnected, isOpen, connect, send]
  );

  // Handle component unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { updateStation, isLoading, error };
}
